import { useMemo, useState } from "react";
import { Trash2, Plus, ChevronDown, ChevronRight, Search } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";
import { fmtEUR, calcTotals, round2 } from "../lib/storage";
import catalog from "../data/catalog.json";

export default function PartidasTable({ partidas, onChange }) {
  const [openCats, setOpenCats] = useState({});
  const grouped = useMemo(() => {
    const g = {};
    (partidas || []).forEach((p, idx) => {
      const cat = p.categoria || "Varios";
      if (!g[cat]) g[cat] = [];
      g[cat].push({ ...p, _idx: idx });
    });
    return g;
  }, [partidas]);

  const totals = calcTotals(partidas);

  const update = (idx, patch) => {
    const next = [...partidas];
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };
  const remove = (idx) => onChange(partidas.filter((_, i) => i !== idx));

  const addFromCatalog = (item) => {
    onChange([
      ...(partidas || []),
      {
        id: `cat-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        codigo: item.codigo,
        descripcion: item.descripcion,
        unidad: item.unidad,
        cantidad: 1,
        precio_unitario: item.precio_unitario,
        categoria: item.categoria,
        is_estimation: false,
      },
    ]);
  };

  const addBlank = () => {
    onChange([
      ...(partidas || []),
      {
        id: `man-${Date.now()}`,
        codigo: "",
        descripcion: "",
        unidad: "ud",
        cantidad: 1,
        precio_unitario: 0,
        categoria: "Varios",
        is_estimation: false,
      },
    ]);
  };

  if (!partidas || partidas.length === 0) {
    return (
      <div className="rx-card p-10 text-center" data-testid="partidas-empty">
        <p className="text-slate-500 mb-4">Aún no hay partidas en este presupuesto.</p>
        <div className="flex gap-2 justify-center">
          <CatalogPicker onPick={addFromCatalog} />
          <Button variant="outline" onClick={addBlank} className="gap-2">
            <Plus className="w-4 h-4" /> Añadir vacía
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="rx-card overflow-hidden" data-testid="partidas-table">
      <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
        <h3 className="font-display font-bold text-lg">Partidas ({partidas.length})</h3>
        <div className="flex gap-2">
          <CatalogPicker onPick={addFromCatalog} />
          <Button variant="outline" size="sm" onClick={addBlank} className="gap-2 border-slate-300">
            <Plus className="w-4 h-4" /> Vacía
          </Button>
        </div>
      </div>

      <div className="divide-y divide-slate-100">
        {Object.entries(grouped).map(([cat, rows]) => {
          const isOpen = openCats[cat] !== false;
          const catTotal = rows.reduce(
            (a, r) => a + (Number(r.cantidad) || 0) * (Number(r.precio_unitario) || 0),
            0
          );
          return (
            <div key={cat}>
              <button
                type="button"
                onClick={() => setOpenCats((s) => ({ ...s, [cat]: !isOpen }))}
                className="w-full flex items-center justify-between px-5 py-3 bg-slate-50 hover:bg-slate-100 transition-colors"
                data-testid={`partidas-cat-${cat}`}
              >
                <div className="flex items-center gap-2">
                  {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  <span className="font-semibold text-sm uppercase tracking-wider text-slate-700">{cat}</span>
                  <span className="text-xs text-slate-400">({rows.length})</span>
                </div>
                <span className="text-sm font-semibold text-blue-600">{fmtEUR(catTotal)}</span>
              </button>
              {isOpen &&
                rows.map((r) => (
                  <PartidaRow key={r.id} p={r} idx={r._idx} update={update} remove={remove} />
                ))}
            </div>
          );
        })}
      </div>

      <div className="px-5 py-5 bg-slate-50 border-t border-slate-200">
        <div className="ml-auto max-w-sm space-y-1.5 text-sm">
          <div className="flex justify-between">
            <span className="text-slate-500">Base imponible</span>
            <span className="font-medium" data-testid="total-base">{fmtEUR(totals.base)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500">IVA (21%)</span>
            <span className="font-medium" data-testid="total-iva">{fmtEUR(totals.iva)}</span>
          </div>
          <div className="flex justify-between pt-2 border-t border-slate-200 text-base">
            <span className="font-display font-bold text-slate-900">TOTAL</span>
            <span className="font-display font-bold text-blue-600 text-xl" data-testid="total-amount">
              {fmtEUR(totals.total)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function PartidaRow({ p, idx, update, remove }) {
  const subtotal = round2((Number(p.cantidad) || 0) * (Number(p.precio_unitario) || 0));
  return (
    <div className="px-5 py-3 grid grid-cols-12 gap-3 items-start hover:bg-slate-50/60 transition-colors" data-testid={`partida-row-${idx}`}>
      <div className="col-span-6">
        <Input
          value={p.descripcion}
          onChange={(e) => update(idx, { descripcion: e.target.value })}
          className="h-9 border-transparent hover:border-slate-200 focus-visible:border-slate-300"
          data-testid={`partida-desc-${idx}`}
        />
        {p.is_estimation && <div className="rx-warning-badge mt-1">Estimación IA · Revisar precio</div>}
        <div className="text-[11px] text-slate-400 mt-1 font-mono pl-3">
          {p.codigo || "—"} · {p.categoria}
        </div>
      </div>
      <div className="col-span-1">
        <Input
          value={p.unidad}
          onChange={(e) => update(idx, { unidad: e.target.value })}
          className="h-9 text-center"
          data-testid={`partida-unit-${idx}`}
        />
      </div>
      <div className="col-span-1">
        <Input
          type="number"
          step="0.01"
          value={p.cantidad}
          onChange={(e) => update(idx, { cantidad: e.target.value })}
          className="h-9 text-right"
          data-testid={`partida-qty-${idx}`}
        />
      </div>
      <div className="col-span-2">
        <Input
          type="number"
          step="0.01"
          value={p.precio_unitario}
          onChange={(e) => update(idx, { precio_unitario: e.target.value })}
          className="h-9 text-right"
          data-testid={`partida-price-${idx}`}
        />
      </div>
      <div className="col-span-1 text-right pt-2 font-semibold text-blue-600" data-testid={`partida-subtotal-${idx}`}>
        {fmtEUR(subtotal)}
      </div>
      <div className="col-span-1 flex justify-end pt-1">
        <button
          type="button"
          onClick={() => remove(idx)}
          aria-label="Eliminar partida"
          className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
          data-testid={`partida-remove-${idx}`}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function CatalogPicker({ onPick }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return catalog.slice(0, 30);
    return catalog
      .filter(
        (c) =>
          c.descripcion.toLowerCase().includes(term) ||
          c.categoria.toLowerCase().includes(term) ||
          c.codigo.toLowerCase().includes(term)
      )
      .slice(0, 50);
  }, [q]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2 border-slate-300" data-testid="catalog-picker-btn">
          <Plus className="w-4 h-4" /> Añadir del catálogo
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[520px] p-0" align="end">
        <div className="p-3 border-b border-slate-200">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
            <Input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar partida, código o categoría…"
              className="pl-9 h-9"
              data-testid="catalog-search-input"
            />
          </div>
        </div>
        <div className="max-h-80 overflow-y-auto divide-y divide-slate-100">
          {filtered.map((c) => (
            <button
              key={c.codigo}
              type="button"
              onClick={() => {
                onPick(c);
                setOpen(false);
              }}
              className="w-full text-left px-4 py-2.5 hover:bg-blue-50 transition-colors flex items-start gap-3"
              data-testid={`catalog-item-${c.codigo}`}
            >
              <span className="font-mono text-[11px] text-slate-400 mt-1">{c.codigo}</span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-800 truncate">{c.descripcion}</div>
                <div className="text-xs text-slate-500">{c.categoria}</div>
              </div>
              <div className="text-sm font-semibold text-blue-600 whitespace-nowrap">
                {fmtEUR(c.precio_unitario)}/{c.unidad}
              </div>
            </button>
          ))}
          {filtered.length === 0 && (
            <div className="p-6 text-center text-sm text-slate-400">Sin resultados</div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
